/**
 * Recently visited organizations and users (GDN-2777).
 *
 * Admins bounce between the same handful of customers, so the useful sidebar
 * affordance is "where was I", not a browse list of every org. The header
 * search already covers "find one by name"; this covers "get back to the one
 * I was just in".
 *
 * Local to the browser on purpose: it's a per-person trail, it has to be
 * instant, and it needs no backend. That does mean it doesn't follow someone
 * to another machine, which is the right trade for a convenience shortcut.
 */

const STORAGE_KEY = 'admin-v2.recent-entities';

/** Long enough to cover a working session, short enough to stay scannable. */
export const MAX_RECENT = 8;

export type RecentEntityKind = 'org' | 'user';

export interface RecentEntity {
  kind: RecentEntityKind;
  id: string;
  /** Display name at the time of the visit; ids alone aren't recognisable. */
  name: string;
  /** Epoch ms, used only for ordering. */
  visitedAt: number;
}

function isRecentEntity(value: unknown): value is RecentEntity {
  if (!value || typeof value !== 'object') return false;
  const entity = value as Partial<RecentEntity>;
  return (
    (entity.kind === 'org' || entity.kind === 'user') &&
    typeof entity.id === 'string' &&
    entity.id.length > 0 &&
    typeof entity.name === 'string' &&
    typeof entity.visitedAt === 'number'
  );
}

/**
 * Add a visit to the front of the trail.
 *
 * Pure so the ordering and dedupe rules are testable without a DOM. An entity
 * revisited moves to the front and keeps one entry rather than accumulating
 * duplicates, and its name refreshes — orgs get renamed.
 */
export function addRecent(
  existing: RecentEntity[],
  entry: RecentEntity,
): RecentEntity[] {
  const rest = existing.filter(
    item => !(item.kind === entry.kind && item.id === entry.id),
  );
  return [entry, ...rest].slice(0, MAX_RECENT);
}

/** Read the trail, newest first. Any malformed entry is dropped, not thrown. */
export function readRecent(storage: Pick<Storage, 'getItem'>): RecentEntity[] {
  try {
    const raw = storage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter(isRecentEntity)
      .sort((a, b) => b.visitedAt - a.visitedAt)
      .slice(0, MAX_RECENT);
  } catch {
    // Corrupt or unavailable storage must not take the sidebar down with it.
    return [];
  }
}

export function writeRecent(
  storage: Pick<Storage, 'setItem'>,
  entities: RecentEntity[],
): void {
  try {
    storage.setItem(STORAGE_KEY, JSON.stringify(entities.slice(0, MAX_RECENT)));
  } catch {
    // Private mode / quota. Losing the trail is not worth an error.
  }
}

export { STORAGE_KEY as RECENT_STORAGE_KEY };

/**
 * Where a recent entry links to — its entity page.
 *
 * Deliberately separate from `scopeHomePath`: that takes a resource scope, and
 * a user entry has no organization to give it. Passing an empty one works only
 * because the function ignores that field, which is the kind of accident that
 * breaks the first time the shape changes.
 */
export function entityHomePath(entity: Pick<RecentEntity, 'kind' | 'id'>): string {
  const segment = entity.kind === 'org' ? 'organization' : 'provider';
  return `/admin-v2/${segment}/${encodeURIComponent(entity.id)}`;
}
